import {
  CustomerTypes,
  IBooking,
  IBookingFromPouch,
  ICustomer,
  IDiaryVenue,
  ISearchingCustomer,
  IVenue,
} from 'shared-types/index';

import UtilsService from 'external-services/utils-service/index';
import PhoneNumberService from 'external-services/phone-number-service/index';
import { phoneNumberTypes } from 'external-services/phone-number-service/phoneNumber.types';

const NS = 'CustomerNormaliseService'; // jshint ignore:line

class CustomerNormaliseService {
  private venue: { country: string; state: string };

  public init(venue: { country: string; state: string }) {
    this.venue = venue;
  }

  public normalizeViewToModel(customer: ICustomer, APIChannel = false): ICustomer {
    delete customer.fullName;
    delete customer.label;
    delete customer.venueIds;

    customer.tags = customer.tags || [];

    if (APIChannel) {
      customer.id = customer._id;
      delete customer._id;
      delete customer._rev;
      delete customer.type;
    } else {
      customer.type = 'customer';
    }

    customer.lastModifiedDate = new Date().toISOString();

    if (customer.birthdayDate) {
      customer.birthdayDate = Number(customer.birthdayDate);
    }
    if (customer.birthdayMonth) {
      customer.birthdayMonth = Number(customer.birthdayMonth);
    }
    if (customer.birthdayYear) {
      customer.birthdayYear = Number(customer.birthdayYear);
    }

    return this.normalizeCustomerPhoneNumber(customer);
  }

  public normalizeModelToView(customer: ICustomer, APIChannel = false): ICustomer {
    // Guard not found data from PouchDB or API
    if (customer == null) {
      return null;
    }

    customer.type = 'customer';

    if (APIChannel) {
      customer._rev = UtilsService.generateRandomCode() + '_' + new Date().getTime().toString();
      customer._id = customer.id;
      delete customer.id;
    } else {
      // Since v 0.9.64 birthday:Date field is deprecated
      // Replaced by number fields birthdayDate, birthdayMonth, birthdayYear
      // This way a customer can specify it's birthday partially
      // NBI-3732 2022/05/03 Horita
      // Removed the code below as it is unnecessary.
      // if (customer.birthday) {
      //     try {
      //         customer.birthday = new Date(customer.birthday);
      //         if (!customer.birthdayDate) {
      //             customer.birthdayDate = customer.birthday.getDate();
      //             customer.birthdayMonth = customer.birthday.getMonth() + 1;
      //             customer.birthdayYear = customer.birthday.getFullYear();
      //         }
      //     } catch (e) {}
      //     delete customer.birthday;
      // }
    }

    // Newly introduced field, it can be null for old data
    customer.noShowCount = customer.noShowCount || 0;
    // Guarantee not null references
    customer.venueIds = customer.venueIds || [];
    customer.tags = customer.tags || [];
    // FullName as visual attribute (removed before save operations)
    customer.fullName = this.getFullName(customer);

    return customer;
  }

  private normalizeCustomerPhoneNumber(customer: ICustomer): ICustomer {
    if (customer.phone) {
      const formattedPhone = PhoneNumberService.normalizePhoneNumber(
        customer.phone,
        phoneNumberTypes.INTERNATIONAL,
        this.venue
      );
      const formattedPhoneNational = PhoneNumberService.normalizePhoneNumber(
        customer.phone,
        phoneNumberTypes.NATIONAL,
        this.venue
      );
      customer.phone = formattedPhone ? formattedPhone : customer.phone;
      customer.phoneNational = formattedPhoneNational
        ? formattedPhoneNational
        : customer.phoneNational;
    } else {
      delete customer.phone;
      delete customer.phoneNational;
    }
    return customer;
  }

  private getFullName(customer: ICustomer): string {
    const names: string[] = [];
    if (customer.firstName) {
      names.push(customer.firstName);
    }
    if (customer.lastName) {
      names.push(customer.lastName);
    }
    return names.join(' ');
  }

  public updateBookingCustomerInfo(
    booking: IBooking | IBookingFromPouch,
    customer: ICustomer
  ): boolean {
    let updated: boolean = false;

    if (customer && booking) {
      const hasCustomerNotes: boolean = customer.notes && customer.notes.length > 0;
      const hasManagerNotes: boolean = customer.managerNotes && customer.managerNotes.length > 0;

      if (booking.customerId !== customer._id) {
        booking.customerId = customer._id;
        updated = true;
      }

      if (booking.firstName !== customer.firstName) {
        booking.firstName = customer.firstName;
        updated = true;
      }

      if (booking.lastName !== customer.lastName) {
        booking.lastName = customer.lastName;
        updated = true;
      }

      if (booking.company !== customer.company) {
        booking.company = customer.company;
        updated = true;
      }

      if (booking.email !== customer.email) {
        booking.email = customer.email;
        updated = true;
      }

      if (booking.phone !== customer.phone) {
        booking.phone = customer.phone;
        updated = true;
      }

      if (booking.hasVisitedMultipleVenues !== customer.hasVisitedMultipleVenues) {
        booking.hasVisitedMultipleVenues = customer.hasVisitedMultipleVenues;
        updated = true;
      }

      if (booking.hasCustomerNotes !== hasCustomerNotes) {
        booking.hasCustomerNotes = hasCustomerNotes;
        updated = true;
      }

      if (booking.hasManagerNotes !== hasManagerNotes) {
        booking.hasManagerNotes = hasManagerNotes;
        updated = true;
      }

      // NBI-6330
      booking.subscribed = customer.subscribed;

      /**
       * To do
       * Should be remove below part.
       */
      // // if the customer is inline ( used for walkin bookings add the phonenumber of the customer to the booking)
      // if (customer.customerType === CustomerTypes.inline && booking.phone !== customer.phone) {
      //   booking.phone = customer.phone;
      //   updated = true;
      // }
    }

    return updated;
  }

  public updateCustomerBirthdayByString(
    customer: ICustomer,
    birthday: string = '' //1-11-2022, 22-2-2021
  ): void {
    const birthdayData: string[] = birthday.split('-');
    if (birthdayData && birthdayData.length === 3) {
      if (Number(birthdayData[0])) customer.birthdayDate = Number(birthdayData[0]);
      else delete customer.birthdayDate;
      if (Number(birthdayData[1])) customer.birthdayMonth = Number(birthdayData[1]);
      else delete customer.birthdayMonth;
      if (Number(birthdayData[2])) customer.birthdayYear = Number(birthdayData[2]);
      else delete customer.birthdayYear;
    }
  }
  public customerToSearchingCustomer = (customer: ICustomer): ISearchingCustomer => {
    const searchingCustomer: ISearchingCustomer = {} as ISearchingCustomer;
    if (customer.firstName) {
      searchingCustomer.firstName = customer.firstName;
    }
    if (customer.lastName) {
      searchingCustomer.lastName = customer.lastName;
    }
    if (customer.email) {
      searchingCustomer.email = customer.email;
    }
    if (customer.phone) {
      searchingCustomer.phone = customer.phone;
    }
    if (customer.company) {
      searchingCustomer.company = customer.company;
    }
    return searchingCustomer;
  };
}

const instance = new CustomerNormaliseService();
export default instance as CustomerNormaliseService;
